import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import JellyfishLoader from './JellyfishLoader';
import './AuthCallback.css';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Processing your request...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get URL parameters from both query string and hash
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        // Combine both parameter sources
        const allParams = new URLSearchParams();
        for (const [key, value] of urlParams) allParams.set(key, value);
        for (const [key, value] of hashParams) allParams.set(key, value);
        
        const code = allParams.get('code');
        const error = allParams.get('error');
        const errorDescription = allParams.get('error_description');
        const success = allParams.get('success');
        const type = allParams.get('type');
        const access_token = allParams.get('access_token');
        const refresh_token = allParams.get('refresh_token');

        console.log('Auth callback parameters:', {
          code: !!code,
          error: !!error,
          success: !!success,
          type,
          access_token: !!access_token,
          refresh_token: !!refresh_token,
          url: window.location.href
        });

        // Handle error from server
        if (error) {
          console.error('Auth callback error:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || 'Authentication failed. Please try again.');
          return;
        }

        // Handle different auth types
        if (type === 'signup') {
          const autoConfirmed = allParams.get('auto_confirmed') === 'true';
          console.log('Signup callback received', autoConfirmed ? '(auto-confirmed)' : '');
          setStatus('processing');
          setMessage(autoConfirmed ? 'Setting up your account...' : 'Verifying your email and setting up your account...');
          
          // Check if user is authenticated
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (session && session.user) {
            console.log('Signup verified for user:', session.user.email);
            
            // Create user profile data
            try {
              // Get user data from user_metadata (should be set during signup)
              const username = session.user.user_metadata?.username;
              const gender = session.user.user_metadata?.gender || 'prefer_not_to_say';
              const country = session.user.user_metadata?.country || 'Unknown';
              const dob = session.user.user_metadata?.dob || new Date('2000-01-01').toISOString();
              
              // Get consent info from user_metadata if present
              const consentType = session.user.user_metadata?.consent_type;
              const consentTextVersion = session.user.user_metadata?.consent_text_version;
              const consentTextSnapshot = session.user.user_metadata?.consent_text_snapshot;
              
              if (!username) {
                console.error('Username not found in user_metadata');
                setStatus('error');
                setMessage('Error: Username not found. Please contact support.');
                return;
              }

              // Check if profile already exists
              const { data: existingProfile } = await supabase
                .from('user_profiles')
                .select('user_id')
                .eq('user_id', session.user.id)
                .maybeSingle();

              if (existingProfile) {
                // Profile exists, update it with the correct values
                console.log('Profile exists, updating with user_metadata values...');
                const { error: updateError } = await supabase
                  .from('user_profiles')
                  .update({
                    email: session.user.email,
                    username: username,
                    gender: gender,
                    country: country,
                    dob: dob
                  })
                  .eq('user_id', session.user.id);

                if (updateError) {
                  console.error('Error updating user_profiles table:', updateError);
                  setStatus('error');
                  setMessage('Error updating profile. Please contact support.');
                  return;
                }
              } else {
                // Profile doesn't exist, insert new one
                const { error: profileError } = await supabase
                  .from('user_profiles')
                  .insert({
                    user_id: session.user.id,
                    email: session.user.email,
                    username: username,
                    date_joined: new Date().toISOString(),
                    gender: gender,
                    country: country,
                    dob: dob,
                    admin_flag: 0
                  });

                if (profileError) {
                  if (profileError.message.includes('duplicate key') || profileError.code === '23505') {
                    // Username or email already exists - this shouldn't happen but handle gracefully
                    if (profileError.message.includes('username')) {
                      console.error('Username already exists:', username);
                      setStatus('error');
                      setMessage('This username is already taken. Please contact support.');
                      return;
                    } else {
                      // Email already exists - profile might already be created, try updating
                      const { error: updateError } = await supabase
                        .from('user_profiles')
                        .update({
                          username: username,
                          gender: gender,
                          country: country,
                          dob: dob
                        })
                        .eq('user_id', session.user.id);
                      
                      if (updateError) {
                        console.error('Error updating profile after conflict:', updateError);
                        setStatus('error');
                        setMessage('Error creating profile. Please contact support.');
                        return;
                      }
                    }
                  } else {
                    console.error('Error inserting into user_profiles table:', profileError);
                    setStatus('error');
                    setMessage('Error creating profile. Please contact support.');
                    return;
                  }
                }
              }

              // Insert consent record if consent info is present in user_metadata
              if (consentType && consentTextVersion && consentTextSnapshot) {
                try {
                  const { error: consentError } = await supabase
                    .from('health_data_consent')
                    .insert({
                      user_id: session.user.id,
                      consent_type: consentType,
                      consent_text_version: consentTextVersion,
                      consent_text_snapshot: consentTextSnapshot,
                      given_at: new Date().toISOString()
                    });

                  if (consentError) {
                    console.error('Error inserting consent record:', consentError);
                    // Don't fail the signup process if consent insertion fails
                    // The consent info is still in user_metadata
                  }
                } catch (consentErr) {
                  console.error('Error inserting consent record:', consentErr);
                  // Don't fail the signup process if consent insertion fails
                }
              }

              setStatus('success');
              setMessage('Account created successfully! Redirecting to dashboard...');
              
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
              
            } catch (dbError) {
              console.error('Database insertion error:', dbError);
              setStatus('success');
              setMessage('Account setup complete! Redirecting to dashboard...');
              
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            }
          } else {
            setStatus('error');
            setMessage('Account setup failed. Please try again.');
          }
          return;
        }

        if (type === 'magiclink') {
          console.log('Magic link callback received');
          setStatus('success');
          setMessage('Magic link authentication successful! Redirecting to dashboard...');
          
          // Check if user is authenticated
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (session && session.user) {
            console.log('Magic link verified for user:', session.user.email);
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            setStatus('error');
            setMessage('Magic link authentication failed. Please try again.');
          }
          return;
        }

        // Handle success from server (fallback)
        if (success === 'true') {
          console.log('Authentication successful via server callback');
          setStatus('success');
          
          // Get current session to verify
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Error getting session:', sessionError);
            setStatus('error');
            setMessage('Session verification failed. Please try again.');
            return;
          }

          if (session && session.user) {
            console.log('Session verified for user:', session.user.email);
            setMessage('Authentication successful! Redirecting to dashboard...');
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            setStatus('error');
            setMessage('No active session found. Please try again.');
          }
          return;
        }

        // Handle direct access_token/refresh_token flow (from hash)
        if (access_token && refresh_token) {
          console.log('Processing direct token flow');
          setStatus('processing');
          setMessage('Setting up your session...');
          
          try {
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token,
              refresh_token
            });

            if (sessionError) {
              console.error('Session error:', sessionError);
              setStatus('error');
              setMessage('Failed to set up session. Please try again.');
              return;
            }

            if (data.session && data.user) {
              console.log('Authentication successful:', data.user.email);
              setStatus('success');
              setMessage('Authentication successful! Redirecting to dashboard...');
              
              // Redirect to dashboard after a short delay
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            } else {
              setStatus('error');
              setMessage('Authentication incomplete. Please try again.');
            }
          } catch (sessionError) {
            console.error('Error setting session:', sessionError);
            setStatus('error');
            setMessage('Failed to complete authentication. Please try again.');
          }
          return;
        }

        // Handle OAuth code flow
        if (code) {
          console.log('Processing auth code:', code);

          // Exchange code for session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('Session exchange error:', exchangeError);
            setStatus('error');
            setMessage('Failed to complete authentication. Please try again.');
            return;
          }

          if (data.session && data.user) {
            console.log('Authentication successful:', data.user.email);
            setStatus('success');
            setMessage('Authentication successful! Redirecting to dashboard...');
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            console.error('No session or user data received');
            setStatus('error');
            setMessage('Authentication incomplete. Please try again.');
          }
          return;
        }

        // No valid parameters found
        console.error('No valid authentication parameters found in URL');
        console.log('Current URL:', window.location.href);
        setStatus('error');
        setMessage('Invalid authentication request. Please try again.');

      } catch (error) {
        console.error('Unexpected error in auth callback:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="auth-callback">
      <div className="auth-callback-container">
        <div className="auth-callback-content">
          <h1 className="status-title">
            {status === 'processing' && 'Processing...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Error'}
          </h1>
          
          <p className="status-message">{message}</p>
          
          {status === 'error' && (
            <div className="error-actions">
              <button 
                className="retry-button"
                onClick={() => navigate('/')}
              >
                Try Again
              </button>
            </div>
          )}
          
          {status === 'processing' && (
            <div className="loading-spinner">
              <JellyfishLoader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;